import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ClipStatus } from "@air/api/types";
import { NetworkStatusInfo } from "@air/classes-network-status-info";
import { clearThumbnailCache, ClipUtils } from "@air/utils-clips";
import { convertUnknownToError, reportErrorToBugsnag } from "@air/utils-error";
import { lazy, memo, Suspense, useMemo } from "react";
import { useSelector } from "react-redux";
import { AssetGalleryCardThumbnailImage } from "~/components/Gallery/AssetGalleryCard/AssetGalleryCardThumbnailImage";
import { SelectedGalleryCardOverlay } from "~/components/Gallery/GalleryCard/SelectedGalleryCardOverlay";
import { getPreferenceClassName, getPreferenceImgixFit, useThumbnailPreference } from "~/hooks/useThumbnailPreference";
import { CentralizedBoardSelectors } from "~/store/centralizedBoard/selectors";
const ClipPreview = /*#__PURE__*/ lazy(async ()=>{
    try {
        const loadedModule = await import("~/components/Shared/ClipList/ClipItem/ClipPreview");
        return {
            default: loadedModule.ClipPreview
        };
    } catch (error) {
        if (NetworkStatusInfo.isOnline) {
            reportErrorToBugsnag({
                error: convertUnknownToError(error),
                context: "Failed to lazy load ClipPreview"
            });
        }
        return Promise.resolve({
            default: ()=>/*#__PURE__*/ _jsx("div", {})
        });
    }
});
const _AssetGalleryCardThumbnail = (param)=>{
    let { clip, isHovering, isSelected, width, height } = param;
    const videoSrc = clip.assets.previewVideo;
    var _clip_duration;
    const playbackRate = ClipUtils.determinePlaybackRate((_clip_duration = clip.duration) !== null && _clip_duration !== void 0 ? _clip_duration : 0, videoSrc !== null && videoSrc !== void 0 ? videoSrc : "");
    const vertical = clip.status === ClipStatus.transcoded && !ClipUtils.isLandscape(clip.rotation);
    const { getPreference } = useThumbnailPreference();
    const boardId = useSelector(CentralizedBoardSelectors.boardId);
    const thumbnailPreference = useMemo(()=>getPreference(boardId), [
        boardId,
        getPreference
    ]);
    const CardThumbnail = useMemo(()=>/*#__PURE__*/ _jsx(AssetGalleryCardThumbnailImage, {
            ...clip,
            className: getPreferenceClassName(thumbnailPreference),
            animatedSrc: clip.type === "animated" && isHovering && videoSrc ? videoSrc : "",
            imgixParams: {
                fit: getPreferenceImgixFit(thumbnailPreference)
            },
            src: clearThumbnailCache({
                clipExt: clip.ext,
                clipStatus: clip.status,
                imageSrc: clip.assets.image,
                clipUpdatedAt: clip.updatedAt
            }),
            width: width,
            height: height
        }), [
        clip,
        isHovering,
        videoSrc,
        thumbnailPreference,
        width,
        height
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "absolute inset-0 flex overflow-hidden rounded",
                children: [
                    CardThumbnail,
                    isSelected && /*#__PURE__*/ _jsx(SelectedGalleryCardOverlay, {})
                ]
            }),
            !!videoSrc && clip.type === "video" && !ClipUtils.isProcessing(clip.status) && isHovering && /*#__PURE__*/ _jsx(Suspense, {
                fallback: CardThumbnail,
                children: /*#__PURE__*/ _jsx(ClipPreview, {
                    vertical: vertical,
                    playbackRate: playbackRate,
                    status: clip.status,
                    src: videoSrc
                })
            })
        ]
    });
};
export const AssetGalleryCardThumbnail = /*#__PURE__*/ memo(_AssetGalleryCardThumbnail);
